import React from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { Section } from '../Section';
import { TransformCTA } from '../TransformCTA';
const before = [{
  title: 'You search for the answer.',
  body: 'You see an abnormal creatinine or eGFR and immediately start looking through different resources. You know the information exists — but finding the right information at the right moment takes time.'
}, {
  title: 'You jump between resources.',
  body: 'Guidelines. References. Medication information. Laboratory interpretation. Referral criteria. You spend valuable consultation time connecting the pieces yourself.'
}, {
  title: 'You second-guess the next step.',
  body: 'Should this be investigated further? What information should be reviewed? What should be monitored? Should treatment be reassessed? Does this warrant specialist evaluation?'
}, {
  title: 'Every abnormal result creates another question.',
  body: 'A change in kidney function. Albuminuria. Potassium. Medication. Progression. Comorbidity. Follow-up. Each scenario can send you down another research path.'
}, {
  title: 'You leave the consultation with unanswered questions.',
  body: 'The patient has left. The consultation is over. But you are still wondering whether you reviewed everything you needed to review.'
}];
const after = ['You identify the situation.', 'You open the corresponding decision pathway.', 'You review the key information.', 'You move through the structured decision points.', 'You identify what needs to be considered next.', 'You establish monitoring and follow-up considerations.', 'You know when specialist evaluation should be considered.'];
export function BeforeAfter() {
  return <Section eyebrow="Before & after" title="Stop improvising when a patient has abnormal kidney function." data-id="element-99" data-mp="jdp56cth4gtz">
      <div className="grid gap-5 lg:grid-cols-2 lg:gap-6" data-id="element-100" data-mp="aety6yilv2pm">
        {/* BEFORE */}
        <div className="flex flex-col rounded-3xl border border-[#e8cfcf] bg-[#fdf4f4] p-5 sm:p-7" data-id="element-101" data-mp="agxy75ddvhkm">
          <div className="flex items-center gap-2.5" data-id="element-102" data-mp="2c63rfqc929i8">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#b3261e]" data-id="element-103" data-mp="27cr89vqcauwv">
              <XIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-104" data-mp="23cqv0eez8pre" />
            </span>
            <h3 className="font-display text-xl font-bold text-[#8c1c2b] sm:text-2xl" data-id="element-105" data-mp="1ydxdmawkkely">
              BEFORE
            </h3>
          </div>
          <p className="mt-2 text-[14px] font-semibold text-[#8c1c2b]/80" data-id="element-106" data-mp="2v10x76iunys8">
            You search for the answer.
          </p>
          <ul className="mt-5 flex-1 space-y-4" data-id="element-107" data-mp="2m26vx2ukqk8c">
            {before.map(item => <li key={item.title} className="rounded-2xl bg-white/70 p-4 ring-1 ring-[#eddcdc]" data-id="element-108" data-mp="2h3cdi20iqxor">
                <p className="text-[14.5px] font-bold leading-snug text-ink" data-id="element-109" data-mp="1ao33i13mx45e">
                  {item.title}
                </p>
                <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft" data-id="element-110" data-mp="15q9xb82c0a54">
                  {item.body}
                </p>
              </li>)}
          </ul>
        </div>

        {/* AFTER */}
        <div className="flex flex-col rounded-3xl border border-[#c9e6d5] bg-[#f2fbf5] p-5 sm:p-7" data-id="element-111" data-mp="10h0kdkaartp6">
          <div className="flex items-center gap-2.5" data-id="element-112" data-mp="1a967ogpltmy1">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-cta" data-id="element-113" data-mp="1a9y3nsn45mou">
              <CheckIcon className="h-4 w-4 text-white" aria-hidden="true" data-id="element-114" data-mp="1e9yhpc6avvvd" />
            </span>
            <h3 className="font-display text-xl font-bold text-[#15603a] sm:text-2xl" data-id="element-115" data-mp="1j8tczdlgfcwd">
              WITH RENAL DECISION DESK™
            </h3>
          </div>
          <p className="mt-2 text-[14px] font-semibold text-[#15603a]/80" data-id="element-116" data-mp="1t80kvl9f6fc3">
            You start with the clinical scenario and follow the route.
          </p>
          <ol className="mt-5 flex-1 space-y-2.5" data-id="element-117" data-mp="3q1vg2hpjq3z3">
            {after.map((step, index) => <li key={step} className="flex items-start gap-3 rounded-2xl bg-white/80 p-4 ring-1 ring-[#d8ece1]" data-id="element-118" data-mp="3uzx83s4y9dmp" data-mpi={index}>
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cta/12 text-[12px] font-extrabold text-cta" data-id="element-119" data-mp="1kcjq3bejkn6m">
                  {index + 1}
                </span>
                <span className="text-[14.5px] font-semibold leading-snug text-ink" data-id="element-120" data-mp="1tc6s9zf3hx0q">
                  {step}
                </span>
              </li>)}
          </ol>
          <p className="mt-5 rounded-2xl bg-[#15603a] px-4 py-4 text-[14px] font-semibold leading-snug text-white" data-id="element-121" data-mp="11lisort2kzp6">
            The result: less searching, less jumping between resources, more
            structured decision-making.
          </p>
        </div>
      </div>

      <TransformCTA label="Yes — I want to use the Renal Decision Desk™" sub="Go from searching to following a structured route • $27 one-time" data-id="element-122" data-mp="1akvy99e2p44m" />
    </Section>;
}